/* lang-stress.js — English WORD STRESS engine for Timbrica /word-stress.
 *
 * Builds on LangIPA (public/js/lang/lang-ipa.js — CMU dict, syllabify, g2p) and
 * adds the three things a best-in-class word-stress tool needs and which no
 * competitor does together:
 *
 *   1. Heteronym resolution by CONTEXT. CMUdict carries noun/verb stress pairs
 *      (REcord/reCORD, PREsent/preSENT). We load cmudict-homographs.json and,
 *      for a grammatical pair, pick the noun- or verb-stressed form from the
 *      surrounding words (a/the/my → noun; to/will/please → verb). Ambiguous →
 *      flagged for the user to switch (like russian-stress homographs).
 *
 *   2. Orthographic syllable split aligned to the phonetic syllable count, so
 *      the stressed syllable can be highlighted IN THE SPELLING (re·CORD), not
 *      only in the IPA.
 *
 *   3. Real stress for out-of-vocabulary words via English suffix rules
 *      (-tion/-ic → penult, -ity/-graphy → antepenult, -ee/-ese → final),
 *      instead of LangIPA's "stress the first vowel" placeholder.
 *
 * UMD: window.LangStress + module.exports. Depends on LangIPA being present in
 * the same realm (browser: loaded by components.lang-loader; Node test: loaded
 * into the same vm sandbox).
 */
(function (root, factory) {
  'use strict';
  var LangStress = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = LangStress;
  if (typeof window !== 'undefined') window.LangStress = LangStress;
  else if (root) root.LangStress = LangStress;
})(typeof self !== 'undefined' ? self : this, function (root) {
  'use strict';

  function getIPA() {
    if (root && root.LangIPA) return root.LangIPA;
    if (typeof window !== 'undefined' && window.LangIPA) return window.LangIPA;
    if (typeof globalThis !== 'undefined' && globalThis.LangIPA) return globalThis.LangIPA;
    return null;
  }

  /* ── Context word-lists for the part-of-speech heuristic ──────────────────*/
  function set(s) { var o = Object.create(null); s.split(' ').forEach(function (w) { o[w] = 1; }); return o; }
  // Before a heteronym, these force the NOUN (early-stress) reading.
  var DET = set('the a an this that these those my your his her its our their whose ' +
    'some any no each every another one no such more most many much several few all ' +
    'both either neither what which another'); // determiners / quantifiers
  // Object of a (non-"to") preposition is a noun.
  var PREP = set('of in on at by for with from into onto about over under after before ' +
    'during through against without within upon per across among between toward towards ' +
    'behind beyond beneath besides despite');
  // These force the VERB (late-stress) reading.
  var VERB_CUE = set("to will would shall should can could may might must do does did " +
    "don't doesn't didn't let let's please cannot won't");
  // Subject pronoun immediately before → usually a verb ("they object", "I record").
  var SUBJ = set('i we you they he she it');
  // Adverbs that typically sit before a verb (weaker verb signal).
  var ADV = set('not also often never always really just still now then carefully ' +
    'quickly suddenly simply gladly kindly formally publicly');

  /* prevWord/nextWord are raw tokens (may carry case/punctuation). Returns
   * {target:'early'|'late', role, confident} or null when context is silent. */
  function resolvePOS(prevWord, nextWord) {
    var pw = prevWord ? prevWord.toLowerCase().replace(/[’‘]/g, "'").replace(/[^a-z']/g, '') : '';
    if (pw) {
      if (VERB_CUE[pw]) return { target: 'late', role: 'verb', confident: true };
      // a modal folded into its subject: I'd like, we'll record, they'd object
      if (/^(i|you|we|they|he|she|it|who|that|there)'(d|ll)$/.test(pw)) return { target: 'late', role: 'verb', confident: true };
      if (DET[pw]) return { target: 'early', role: 'noun', confident: true };
      if (/['’]s$/.test(prevWord) || /s['’]$/.test(prevWord)) return { target: 'early', role: 'noun', confident: true }; // possessive
      if (PREP[pw]) return { target: 'early', role: 'noun', confident: true };
      if (SUBJ[pw]) return { target: 'late', role: 'verb', confident: true };
      if (ADV[pw]) return { target: 'late', role: 'verb', confident: false };
    }
    // A following determiner/article means this word modifies a noun → early
    // ("perfect the" no; "perfect plan" — can't see POS of next; keep weak).
    var nw = nextWord ? nextWord.toLowerCase().replace(/[^a-z']/g, '') : '';
    if (nw && (nw === 'of') ) return { target: 'early', role: 'noun', confident: false }; // "the record of"
    return null;
  }

  /* ── Out-of-vocabulary stress: pick the stressed syllable by suffix ───────*/
  /* w: cleaned lowercase spelling, n: syllable (vowel) count. Returns 0-based
   * stressed-syllable index. English Latinate stress regularities. */
  function oovStressSyllable(w, n) {
    if (n <= 1) return 0;
    var last = n - 1, penult = n - 2, ante = n >= 3 ? n - 3 : 0;
    // final-stress endings
    if (/(ee|eer|ese|esque|aire|oon|een|ette|ique|elle)$/.test(w)) return last;
    // antepenult-pull endings
    if (/(ical|ically|igraphy)$/.test(w)) return ante;
    if (/(ity|ety|ify|ogy|ogist|omy|onomy|graphy|phony|cracy|logy|pathy|nomy|scopy|tomy|ular|ulum|ulous|itude|itute)$/.test(w)) return ante;
    // penult-pull endings (the big Latinate class)
    if (/(ic|ics|tion|sion|cion|tian|cian|ian|ial|ual|ient|ience|ious|eous|uous|cial|tial|tious|cious|ish|ity?)$/.test(w)) return penult;
    if (/(ate|ade|ine|ade)$/.test(w) && n >= 3) return ante;     // gradu-ate, alie-nate → antepenult
    // Germanic default: 2-syllable → first; longer → antepenult.
    return n === 2 ? 0 : ante;
  }

  // Apply a chosen stress syllable to an ARPABET token array (mutates a copy).
  function applyStress(arpa, syllable) {
    var out = arpa.slice(), v = 0;
    for (var i = 0; i < out.length; i++) {
      var m = /(\d)$/.exec(out[i]);
      if (!m) continue;                       // consonant
      var bare = out[i].slice(0, -1);
      out[i] = bare + (v === syllable ? '1' : '0');
      v++;
    }
    return out;
  }

  /* ── Orthographic syllabification aligned to phonetic syllable count ──────*/
  var VOWEL_LETTERS = 'aeiouy';
  var DIGRAPH_ONSET = { bl:1, br:1, ch:1, cl:1, cr:1, dr:1, fl:1, fr:1, gl:1, gr:1,
    ph:1, pl:1, pr:1, sc:1, sh:1, sk:1, sl:1, sm:1, sn:1, sp:1, st:1, sw:1, th:1,
    tr:1, tw:1, wh:1, wr:1, qu:1, kn:1, gn:1, sch:1, scr:1, spl:1, spr:1, str:1, thr:1 };

  function isVowelChar(c, i) {
    if (c === 'y') return i > 0;              // 'y' is a vowel except word-initial
    return VOWEL_LETTERS.indexOf(c) >= 0 && c !== 'y';
  }

  /* Returns an array of spelling chunks (syllables). Tries to match targetN
   * (phonetic syllables); falls back to its own nucleus count. Keeps original
   * case in the output chunks. */
  function orthoSyllables(word, targetN) {
    var letters = word.split('');
    var lc = word.toLowerCase();
    var n = letters.length;
    if (n <= 1) return [word];

    // 1) find vowel-letter nuclei (merge adjacent vowels into one nucleus)
    var nuclei = [];                          // each: {start,end} letter range of the vowel run
    var i = 0;
    while (i < n) {
      if (isVowelChar(lc[i], i)) {
        var s = i;
        while (i < n && isVowelChar(lc[i], i)) i++;
        nuclei.push({ start: s, end: i - 1 });
      } else i++;
    }
    if (nuclei.length <= 1) return [word];

    // 2) silent final 'e' — a trailing "…<consonant>e" e isn't its own syllable
    if (nuclei.length > 1) {
      var lastNuc = nuclei[nuclei.length - 1];
      if (lastNuc.start === lastNuc.end && lc[lastNuc.start] === 'e' && lastNuc.start === n - 1) {
        var prevCh = lc[n - 2];
        if (prevCh && VOWEL_LETTERS.indexOf(prevCh) < 0 && prevCh !== 'l' /* keep -ble/-tle */) {
          nuclei.pop();                       // merge silent e into previous syllable
        }
      }
    }
    if (nuclei.length <= 1) return [word];

    // 3) if too many nuclei vs phonetics, merge the closest adjacent pair(s)
    while (targetN && nuclei.length > targetN && nuclei.length > 1) {
      // merge the pair with the smallest consonant gap between them
      var best = 0, bestGap = Infinity;
      for (var k = 0; k < nuclei.length - 1; k++) {
        var gap = nuclei[k + 1].start - nuclei[k].end - 1;
        if (gap < bestGap) { bestGap = gap; best = k; }
      }
      nuclei[best] = { start: nuclei[best].start, end: nuclei[best + 1].end };
      nuclei.splice(best + 1, 1);
    }

    // 4) choose a cut point in the consonant cluster between each nucleus pair
    var cuts = [0];
    for (var p = 0; p < nuclei.length - 1; p++) {
      var cStart = nuclei[p].end + 1, cEnd = nuclei[p + 1].start; // [cStart,cEnd)
      var clusterLen = cEnd - cStart;
      var cut;
      if (clusterLen <= 0) {
        cut = cEnd;                            // adjacent nuclei (rare after merge)
      } else if (clusterLen === 1) {
        cut = cStart;                          // V·CV (open) — re·cord, ti·ger
      } else {
        // 2+ consonants: keep a legal onset (digraph/blend) with the next syllable
        var two = lc.substr(cEnd - 2, 2), three = lc.substr(cEnd - 3, 3);
        if (clusterLen >= 3 && DIGRAPH_ONSET[three]) cut = cEnd - 3;
        else if (DIGRAPH_ONSET[two]) cut = cEnd - 2; // V·CCV (a·gree, mi·cro)
        else cut = cStart + Math.floor(clusterLen / 2); // VC·CV (ber·ry, win·dow)
      }
      cuts.push(cut);
    }
    cuts.push(n);

    var out = [];
    for (var c = 0; c < cuts.length - 1; c++) {
      var chunk = word.slice(cuts[c], cuts[c + 1]);
      if (chunk) out.push(chunk);
    }
    return out.length ? out : [word];
  }

  /* ── Homograph dictionary (lazy) ──────────────────────────────────────────*/
  var _base = '/data/lang/ipa';
  var _homo = null;          // base word → {type, variants:[{arpa,stress,role}]}
  var _homoPromise = null;

  function load(opts) {
    if (opts && opts.base) _base = opts.base.replace(/\/$/, '');
    var IPA = getIPA();
    var ipaLoad = IPA && IPA.load ? IPA.load({ base: _base }) : Promise.resolve();
    if (!_homoPromise) {
      _homoPromise = fetch(_base + '/cmudict-homographs.json')
        .then(function (r) { if (!r.ok) throw new Error('homographs ' + r.status); return r.json(); })
        .then(function (j) { _homo = j; return true; })
        .catch(function (e) { _homo = {}; return false; }); // degrade: no heteronym data
    }
    return Promise.all([ipaLoad, _homoPromise]).then(function () { return true; });
  }

  function normalize(word) { return String(word || '').toLowerCase().replace(/[^a-z']/g, ''); }

  /* ── Build one analysed word token ────────────────────────────────────────*/
  function buildFromArpa(IPA, token, arpa, source, accent) {
    var r = IPA.assemble(token, arpa, source, { accent: accent || 'us' });
    var syl = orthoSyllables(token, r.syllables.length);
    var stressIndex = r.stressIndex;
    if (stressIndex < 0 && r.syllables.length === 1) stressIndex = 0; // monosyllable is stressed
    var secondary = -1;
    for (var i = 0; i < r.stress.length; i++) { if (r.stress[i] === 2) { secondary = i; break; } }
    // map phonetic stress index onto the ortho chunks (clamp if counts differ)
    var orthoStress = stressIndex;
    if (orthoStress >= syl.length) orthoStress = syl.length - 1;
    var orthoSecondary = secondary;
    if (orthoSecondary >= syl.length) orthoSecondary = -1;
    return {
      ipa: r.ipa,
      ipaSyllables: r.syllables,
      syllables: syl,
      stressIndex: orthoStress,
      phoneticStressIndex: stressIndex,
      secondaryIndex: orthoSecondary,
      syllableCount: r.syllables.length,
      arpa: arpa.slice(),
      source: source,
      approximate: source === 'fallback',
    };
  }

  // contextPrev / contextNext = neighbouring raw word tokens (for POS).
  function analyzeWord(token, contextPrev, contextNext, accent) {
    var IPA = getIPA();
    var base = normalize(token);
    if (!IPA || !base) {
      return { token: token, isWord: true, syllables: [token], stressIndex: 0,
        secondaryIndex: -1, ipa: '', source: 'none', approximate: true, syllableCount: 1 };
    }

    var het = _homo && _homo[base];
    if (het && het.variants && het.variants.length > 1) {
      // build every variant, then pick by context
      var variants = het.variants.map(function (v) {
        var d = buildFromArpa(IPA, token, v.arpa.split(' '), 'homograph', accent);
        d.role = v.role || null;
        return d;
      });
      // sort by phonetic stress so index 0 = earliest stress
      variants.sort(function (a, b) { return a.phoneticStressIndex - b.phoneticStressIndex; });

      var chosen = 0, autoResolved = false;
      if (het.type === 'gram') {
        var pos = resolvePOS(contextPrev, contextNext);
        if (pos) {
          chosen = pos.target === 'late' ? variants.length - 1 : 0;
          autoResolved = pos.confident;
        }
      }
      var picked = variants[chosen];
      var result = Object.assign({}, picked, {
        token: token, isWord: true,
        isHeteronym: true, hetType: het.type,
        variants: variants, chosen: chosen, autoResolved: autoResolved,
        role: picked.role,
      });
      return result;
    }

    // ordinary path through LangIPA
    var hit = IPA.toIPASync ? IPA.toIPASync(token, {}) : null;
    if (hit && hit.source === 'cmudict') {
      var d1 = buildFromArpa(IPA, token, hit.arpabet, 'cmudict', accent);
      return Object.assign({ token: token, isWord: true, isHeteronym: false }, d1);
    }
    // OOV: re-stress the g2p output with English suffix rules. toIPASync already
    // ran g2p for a miss (source 'fallback'); reuse its arpabet instead of a 2nd call.
    var arpa = (hit && hit.arpabet) ? hit.arpabet : IPA.g2p(token).arpabet;
    var nVowels = arpa.filter(function (p) { return /\d$/.test(p); }).length || 1;
    var fixed = applyStress(arpa, oovStressSyllable(base, nVowels));
    var d2 = buildFromArpa(IPA, token, fixed, 'fallback', accent);
    return Object.assign({ token: token, isWord: true, isHeteronym: false }, d2);
  }

  /* Set the chosen variant of a heteronym token (user override). Returns the
   * updated flat fields so the caller can re-render that word. */
  function pick(tokenObj, variantIndex) {
    if (!tokenObj || !tokenObj.variants || !tokenObj.variants[variantIndex]) return tokenObj;
    var picked = tokenObj.variants[variantIndex];
    tokenObj.chosen = variantIndex;
    tokenObj.autoResolved = true;            // user made it explicit
    tokenObj.stressIndex = picked.stressIndex;
    tokenObj.secondaryIndex = picked.secondaryIndex;
    tokenObj.syllables = picked.syllables;
    tokenObj.ipa = picked.ipa;
    tokenObj.role = picked.role;
    tokenObj.source = picked.source;
    return tokenObj;
  }

  /* ── Analyse a whole text ──────────────────────────────────────────────────*/
  // Returns array of tokens: words get the analyseWord shape; non-words get
  // {token, space:true} or {token, punct:true}.
  function tokenizeRaw(text) {
    return String(text || '').match(/[A-Za-z]+(?:['’][A-Za-z]+)*|\s+|[^A-Za-z'’\s]+/g) || [];
  }

  function analyze(text, opts) {
    var IPA = getIPA();
    var accent = (opts && opts.accent) || 'us';
    var raw = tokenizeRaw(text);
    // ensure shards needed are loaded (LangIPA.phrase loads by first letter)
    var pre = (IPA && IPA.load) ? IPA.load({ base: _base }) : Promise.resolve();
    return Promise.all([pre, _homoPromise || Promise.resolve()]).then(function () {
      // preload shards for all words via LangIPA.phrase (handles fetch); ignore result shape
      var ensure = (IPA && IPA.phrase) ? IPA.phrase(text).catch(function () { return null; }) : Promise.resolve(null);
      return ensure.then(function () {
        var out = [], words = [];
        for (var i = 0; i < raw.length; i++) {
          if (/^\s+$/.test(raw[i])) { out.push({ token: raw[i], space: true }); continue; }
          if (!/[A-Za-z]/.test(raw[i])) { out.push({ token: raw[i], punct: true }); continue; }
          words.push({ raw: raw[i], outIndex: out.length });
          out.push(null); // placeholder
        }
        for (var w = 0; w < words.length; w++) {
          var prev = w > 0 ? words[w - 1].raw : null;
          var next = w < words.length - 1 ? words[w + 1].raw : null;
          out[words[w].outIndex] = analyzeWord(words[w].raw, prev, next, accent);
        }
        return out;
      });
    });
  }

  return {
    load: load,
    analyze: analyze,
    analyzeWord: analyzeWord,           // (token, prevWord, nextWord)
    pick: pick,
    // exposed for unit tests:
    orthoSyllables: orthoSyllables,
    oovStressSyllable: oovStressSyllable,
    applyStress: applyStress,
    resolvePOS: resolvePOS,
    normalize: normalize,
    get ready() { return !!_homo; },
  };
});
